/**
 * useAudioCapture — React hook for microphone capture and PCM streaming.
 *
 * EPIC-01-04 AC-2: Captures microphone audio via getUserMedia and sends it
 * as ``{ type: "audio_chunk", data: "<base64 PCM>" }`` WebSocket messages.
 *
 * EPIC-01-04 AC-3: Sends ``{ type: "end_turn", data: {} }`` when
 * ``stopCapture()`` is called (agent finished speaking).
 *
 * ## Audio pipeline
 * ```
 * getUserMedia({ audio: true })
 *   → MediaStream
 *   → AudioContext.createMediaStreamSource()
 *   → ScriptProcessorNode (bufferSize=4096, 1 channel → 1 channel)
 *   → onaudioprocess: Float32Array → Int16Array (PCM-16) → base64 → WS send
 * ```
 *
 * ScriptProcessorNode is deprecated in favour of AudioWorkletNode, but
 * AudioWorklet requires a worker URL / WASM boundary which complicates the
 * local-dev and test setup.  ScriptProcessor works in all browsers supported
 * by ARIA (Chrome, Edge, Safari 15+) and will be migrated in a future sprint.
 *
 * ## Silence detection
 * Frames with RMS energy < SILENCE_THRESHOLD are skipped (not sent over WS)
 * to avoid sending continuous silence and wasting bandwidth / STT quota.
 *
 * ## Resampling
 * AudioContext defaults to the device sample rate (usually 44 100 Hz or
 * 48 000 Hz).  Amazon Transcribe requires 16 000 Hz PCM.  We downsample by
 * taking every N-th sample where N = floor(deviceRate / 16 000).  This simple
 * approach introduces minor aliasing at the cost of zero dependencies; an FFT-
 * based polyphase filter would be more accurate but is out of scope for MVP.
 *
 * ## i18n / ARIA compliance
 * - No raw transcript text is logged (PII constraint — CLAUDE.md §PII).
 * - All user-visible strings are returned as enum state values; the
 *   component layer translates them via react-i18next.
 *
 * @module useAudioCapture
 */

import { useCallback, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Message shape sent to the WebSocket (mirrors server-side protocol). */
export interface WsClientMessage {
  type: 'audio_chunk' | 'end_turn';
  data: string | Record<string, unknown>;
}

/** Microphone access states. */
export type MicState = 'idle' | 'capturing' | 'denied' | 'unsupported' | 'error';

export interface UseAudioCaptureOptions {
  /** Callback that sends a typed JSON message over the active WebSocket. */
  sendMessage: (msg: WsClientMessage) => void;
  /** Whether the WebSocket is currently open and ready to receive messages. */
  isConnected: boolean;
  /**
   * Optional gate. When it returns true (the AI is currently speaking) the mic
   * runs in barge-in mode: low-energy frames (residual echo of the AI's own
   * voice) are dropped to avoid the speak→mic→STT feedback loop, but clearly
   * voiced frames (the agent talking over the AI) ARE sent so the agent can
   * interrupt. Capture always keeps running so the visualizer + end_turn work.
   */
  isOutputActive?: () => boolean;
  /**
   * Called once when the agent starts speaking over the AI (barge-in detected).
   * The page wires this to cancel AI audio playback so the AI is cut off
   * immediately instead of finishing its buffered reply.
   */
  onBargeIn?: () => void;
}

export interface UseAudioCaptureReturn {
  /** True while microphone is open and audio is being streamed. */
  isCapturing: boolean;
  /** Current microphone permission / operational state. */
  micState: MicState;
  /** Open the microphone and begin streaming audio chunks. */
  startCapture: () => Promise<void>;
  /** Close the microphone, flush pending audio, and send end_turn. */
  stopCapture: () => void;
  /** The live capture MediaStream (for visualization). Null when not capturing. */
  getMediaStream: () => MediaStream | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Output sample rate for Amazon Transcribe (PCM-16 mono). */
const TARGET_SAMPLE_RATE = 16_000;

/**
 * ScriptProcessorNode buffer size in samples at the device sample rate.
 * 4 096 samples @ 44.1 kHz ≈ 93 ms per callback.  Latency is acceptable for
 * real-time speech; smaller values increase CPU pressure without benefiting STT.
 */
const SCRIPT_PROCESSOR_BUFFER_SIZE = 4_096;

/**
 * RMS energy threshold below which a frame is considered silence and skipped.
 * Range: [0, 1].  0.005 rejects frames that are essentially noise-floor.
 */
const SILENCE_THRESHOLD = 0.0001;

/**
 * RMS energy above which a frame captured WHILE THE AI IS SPEAKING is treated
 * as the agent barging in (rather than residual echo of the AI's own voice,
 * which browser echo-cancellation keeps low). Tuned conservatively; on open
 * speakers loud AI audio can still approach this, so headphones remain ideal.
 */
const BARGE_IN_THRESHOLD = 0.06;

// ---------------------------------------------------------------------------
// PCM helpers
// ---------------------------------------------------------------------------

/**
 * Downsample a Float32 audio buffer from ``sourceSampleRate`` to
 * ``TARGET_SAMPLE_RATE`` by simple decimation (every N-th sample).
 *
 * @param buffer  - Raw Float32 samples from ScriptProcessorNode.
 * @param sourceSampleRate - Device AudioContext sample rate (e.g. 44 100).
 * @returns Downsampled Float32Array at TARGET_SAMPLE_RATE.
 */
function downsample(buffer: Float32Array, sourceSampleRate: number): Float32Array {
  if (sourceSampleRate === TARGET_SAMPLE_RATE) return buffer;
  const ratio = Math.floor(sourceSampleRate / TARGET_SAMPLE_RATE);
  const outputLength = Math.floor(buffer.length / ratio);
  const output = new Float32Array(outputLength);
  for (let i = 0; i < outputLength; i++) {
    output[i] = buffer[i * ratio] ?? 0;
  }
  return output;
}

/**
 * Convert Float32 PCM samples (range –1 … +1) to 16-bit signed little-endian
 * PCM bytes (range –32 768 … +32 767).
 *
 * @param samples - Downsampled Float32 audio.
 * @returns Raw PCM-16 bytes in little-endian order.
 */
function float32ToPcm16(samples: Float32Array): Uint8Array {
  const buffer = new ArrayBuffer(samples.length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < samples.length; i++) {
    const clamped = Math.max(-1, Math.min(1, samples[i] ?? 0));
    view.setInt16(i * 2, clamped * 0x7fff, /* littleEndian */ true);
  }
  return new Uint8Array(buffer);
}

/**
 * Compute the root-mean-square energy of a Float32 audio buffer.
 * Returns 0 for empty buffers.
 */
function rmsEnergy(buffer: Float32Array): number {
  if (buffer.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < buffer.length; i++) {
    const s = buffer[i] ?? 0;
    sum += s * s;
  }
  return Math.sqrt(sum / buffer.length);
}

/**
 * Encode a Uint8Array as a base64 string.
 *
 * ``btoa`` requires a binary string — we use String.fromCharCode so that
 * multi-byte values (> 127) are encoded correctly.
 */
function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i] ?? 0);
  }
  return btoa(binary);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Captures microphone audio and streams PCM frames over the provided WebSocket
 * send callback.
 *
 * @example
 * ```tsx
 * const { isCapturing, micState, startCapture, stopCapture } = useAudioCapture({
 *   sendMessage: (msg) => ws.send(JSON.stringify(msg)),
 *   isConnected: wsStatus === 'connected',
 * });
 * ```
 */
export function useAudioCapture({
  sendMessage,
  isConnected,
  isOutputActive,
  onBargeIn,
}: UseAudioCaptureOptions): UseAudioCaptureReturn {
  const [isCapturing, setIsCapturing] = useState(false);
  const [micState, setMicState] = useState<MicState>('idle');

  // Latest gate + barge-in callbacks in refs so the ScriptProcessor closure
  // (created once per capture) always calls the current versions.
  const isOutputActiveRef = useRef<(() => boolean) | undefined>(isOutputActive);
  isOutputActiveRef.current = isOutputActive;
  const onBargeInRef = useRef<(() => void) | undefined>(onBargeIn);
  onBargeInRef.current = onBargeIn;

  // Refs so closures inside ScriptProcessorNode callback always see latest values
  const audioCtxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const framesSentRef = useRef<number>(0);

  // -------------------------------------------------------------------------
  // startCapture
  // -------------------------------------------------------------------------

  const startCapture = useCallback(async (): Promise<void> => {
    // No-op if already capturing or WS not open
    if (isCapturing) return;

    // Check getUserMedia support
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      setMicState('unsupported');
      return;
    }

    try {
      // Enable the browser's built-in voice processing. echoCancellation is
      // defense-in-depth against the AI's voice leaking into the mic (the
      // half-duplex gate below is the primary fix); noiseSuppression +
      // autoGainControl improve STT quality. Headphones remain the gold standard.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      // AudioContext — browser default sample rate (44 100 or 48 000 Hz).
      // Chrome suspends new AudioContext instances by default.  We call resume()
      // here because startCapture() is invoked from the mic button onClick via
      // primeAudioContext().then(() => startCapture()), which executes inside
      // the user-gesture microtask queue — Chrome accepts the resume() call.
      // Without this, ScriptProcessorNode fires but the audio graph is suspended
      // and no audio data reaches onaudioprocess in Chrome.
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      const source = ctx.createMediaStreamSource(stream);

      // ScriptProcessorNode: 1 input channel → 1 output channel
      const processor = ctx.createScriptProcessor(
        SCRIPT_PROCESSOR_BUFFER_SIZE,
        /* inputChannels */ 1,
        /* outputChannels */ 1,
      );
      processorRef.current = processor;

      processor.onaudioprocess = (event: AudioProcessingEvent): void => {
        const rawSamples = event.inputBuffer.getChannelData(0);
        const energy = rmsEnergy(rawSamples);
        const aiSpeaking = isOutputActiveRef.current?.() ?? false;

        if (aiSpeaking) {
          // Barge-in mode: drop low-energy frames (residual echo of the AI's
          // own voice) so the AI isn't re-transcribed, but let clearly-voiced
          // frames through so the agent can interrupt. On the first such frame,
          // fire onBargeIn so the page cuts off the AI's audio immediately.
          if (energy < BARGE_IN_THRESHOLD) return;
          onBargeInRef.current?.();
        } else if (energy < SILENCE_THRESHOLD) {
          // Normal listening: skip ambient silence to save bandwidth/STT quota.
          return;
        }

        const downsampled = downsample(rawSamples, ctx.sampleRate);
        const pcm16 = float32ToPcm16(downsampled);
        const encoded = uint8ToBase64(pcm16);

        sendMessage({ type: 'audio_chunk', data: encoded });

        // Instrumentation: confirm the browser is actually emitting audio frames.
        framesSentRef.current += 1;
        if (framesSentRef.current === 1) {
          console.info('[mic] first audio_chunk sent — capture is streaming');
        } else if (framesSentRef.current % 25 === 0) {
          console.info(`[mic] audio_chunk frames sent: ${framesSentRef.current}`);
        }
      };

      // Connect graph: source → processor → destination
      // The processor must be connected to destination to fire onaudioprocess
      // in Chrome (even though we don't use the output).
      source.connect(processor);
      processor.connect(ctx.destination);

      framesSentRef.current = 0;
      console.info('[mic] capture started — sample rate:', ctx.sampleRate, 'state:', ctx.state);
      setIsCapturing(true);
      setMicState('capturing');
    } catch (err: unknown) {
      const name = err instanceof Error ? err.name : '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setMicState('denied');
      } else {
        setMicState('error');
      }
      setIsCapturing(false);
    }
  }, [isCapturing, sendMessage]);

  // -------------------------------------------------------------------------
  // stopCapture
  // -------------------------------------------------------------------------

  const stopCapture = useCallback((): void => {
    // Disconnect and close ScriptProcessorNode
    if (processorRef.current) {
      processorRef.current.onaudioprocess = null;
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    // Stop all MediaStream tracks (releases microphone hardware lock)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }

    // Close AudioContext
    if (audioCtxRef.current) {
      void audioCtxRef.current.close();
      audioCtxRef.current = null;
    }

    // Notify pipeline that the agent's turn is complete (AC-3)
    if (isConnected) {
      sendMessage({ type: 'end_turn', data: {} });
    }

    setIsCapturing(false);
    setMicState('idle');
  }, [isConnected, sendMessage]);

  return {
    isCapturing,
    micState,
    startCapture,
    stopCapture,
    getMediaStream: () => streamRef.current,
  };
}
